import React, { useState, useEffect } from 'react';
import {
  ChipDirective,
  ChipListComponent,
  ChipsDirective,
} from '@syncfusion/ej2-react-buttons';
import OpTextBox from 'op2mise-react-widgets/op-textbox/OpTextBox';
import './OpChipsWithInput.css';

/**
 * OpChipsWithInput - A component that combines a list of removable chips with a text input.
 *
 * @param {string} [label] - Optional label displayed above the input group.
 * @param {string[]} [value] - Array of strings representing the initial/current chips.
 * @param {function} [onChange] - Callback fired when a chip is added or removed.
 * @param {string} [placeholder='Type and press enter...'] - Placeholder text.
 * @param {string} [type='text'] - Input type. If set to 'email', applies email validation on Enter.
 */
export function OpChipsWithInput({
  label,
  onChange,
  placeholder = 'Type and press enter...',
  type = 'email',
  value = [],
}) {
  // Manage chips internal state to allow standalone usage while syncing with parent
  const [internalValues, setInternalValues] = useState(value);
  // Manage the current text typed in the OpTextBox
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setInternalValues(value);
  }, [JSON.stringify(value)]);

  const handleInputChange = (val) => {
    setInputValue(val);

    // Clear the error message as soon as the user starts typing again
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleAddChip = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const newChipText = inputValue.trim();

      // Do nothing if input is empty
      if (!newChipText) return;

      // --- EMAIL VALIDATION LOGIC ---
      if (type === 'email') {
        // Simple standard email regex validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(newChipText)) {
          setErrorMessage('Invalid email address');
          return; // Exit early, preventing the chip from being added
        }
      }

      // Add the chip if it's not a duplicate
      if (!internalValues.includes(newChipText)) {
        const updatedValues = [...internalValues, newChipText];

        setInternalValues(updatedValues);
        setInputValue('');
        setErrorMessage(''); // Clear any lingering errors

        if (onChange) {
          onChange(updatedValues);
        }
      } else {
        // Clear input if it was a duplicate, or show a duplicate error
        setInputValue('');
      }
    }
  };

  const handleDeleteChip = (args) => {
    const deletedChipText = args.text;
    const updatedValues = internalValues.filter(
      (chip) => chip !== deletedChipText,
    );

    setInternalValues(updatedValues);

    if (onChange) {
      onChange(updatedValues);
    }
  };

  return (
    <div className="op-chips-with-input-wrapper">
      {label && <span className="op-chips-with-input-label">{label}</span>}

      <div className="op-chips-with-input-group-wrapper">
        {internalValues.length > 0 && (
          <ChipListComponent
            className="op-chips-with-input-buttons"
            enableDelete
            deleted={handleDeleteChip}
          >
            <ChipsDirective>
              {internalValues.map((chipText, index) => (
                <ChipDirective key={`${chipText}-${index}`} text={chipText} />
              ))}
            </ChipsDirective>
          </ChipListComponent>
        )}

        <OpTextBox
          errorMessage={errorMessage} // Passes the error to trigger OpTextBox's red border/text
          onChange={handleInputChange}
          onKeyDown={handleAddChip}
          placeholder={placeholder}
          value={inputValue}
        />
      </div>
    </div>
  );
}
